import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
    Search,
    Moon,
    Sun,
    Menu,
    X,
    Database,
    TrendingUp,
    Users,
    Heart,
    GraduationCap,
    Building,
    Wheat,
    Factory,
    Code,
    BookOpen,
    TestTube,
    LayoutDashboard,
    ArrowRight,
    Filter,
    ChevronDown,
    Play,
    Copy,
    RotateCcw,
    ExternalLink,
    Globe,
    Zap,
    Activity,
    Clock,
    Briefcase,
    BarChart3,
    MapPin,
    Upload
} from 'lucide-react';
import {
    LineChart,
    Line,
    BarChart,
    Bar,
    PieChart,
    Pie,
    Cell,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from 'recharts';

import './INSDAPIPortal.css';

// Main App Component
function INSDAPIPortal() {
    const navigate = useNavigate();
    const [darkMode, setDarkMode] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('poverty');
    const [selectedEndpoint, setSelectedEndpoint] = useState('/api/v1/demographics/population');
    const [apiResponse, setApiResponse] = useState(null);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filters, setFilters] = useState([]);
    const [currentLang, setCurrentLang] = useState('fr');
    const [codeTab, setCodeTab] = useState('curl');
    const [selectedApi, setSelectedApi] = useState(null);
    const [showApiModal, setShowApiModal] = useState(false);

    // Référence pour le défilement vers la console
    const consoleSectionRef = useRef(null);

    // Toggle dark mode
    useEffect(() => {
        if (darkMode) {
            document.documentElement.classList.add('dark');
        } else {
            document.documentElement.classList.remove('dark');
        }
    }, [darkMode]);

    // Fonction pour défiler vers la console
    const scrollToConsole = () => {
        if (consoleSectionRef.current) {
            setTimeout(() => {
                consoleSectionRef.current.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start'
                });
            }, 100); // Petit délai pour permettre le rendu
        }
    };

    // Sample data for visualizations
    const povertyData = [
        { year: '2015', rate: 40.1 },
        { year: '2017', rate: 41.4 },
        { year: '2019', rate: 42.0 },
        { year: '2021', rate: 43.2 },
        { year: '2023', rate: 44.5 },
    ];

    const cottonData = [
        { year: '2020', production: 1.85 },
        { year: '2021', production: 2.02 },
        { year: '2022', production: 1.95 },
        { year: '2023', production: 2.14 },
        { year: '2024', production: 2.25 },
    ];

    const sectorData = [
        { name: 'Agriculture', value: 35, color: '#00A86B' },
        { name: 'Services', value: 42, color: '#1890ff' },
        { name: 'Industrie', value: 23, color: '#FCD116' },
    ];

    const healthData = [
        { indicator: 'Mortalité infantile', value: 48, unit: '‰', trend: 'down' },
        { indicator: 'Espérance de vie', value: 62, unit: 'ans', trend: 'up' },
        { indicator: 'Vaccination', value: 87, unit: '%', trend: 'up' },
        { indicator: 'Accès eau potable', value: 69.9, unit: '%', trend: 'up' },
    ];

    // API endpoints with detailed information
    const endpointsData = {
        demographics: [
            { value: '/api/v1/demographics/population', label: 'Population par région' },
            { value: '/api/v1/demographics/density', label: 'Densité démographique' },
            { value: '/api/v1/demographics/age-structure', label: 'Structure par âge' },
        ],
        economy: [
            { value: '/api/v1/economy/gdp', label: 'PIB et croissance' },
            { value: '/api/v1/economy/inflation', label: 'Taux d\'inflation' },
            { value: '/api/v1/economy/employment', label: 'Emploi et chômage' },
        ],
        agriculture: [
            { value: '/api/v1/agriculture/cotton', label: 'Production de coton' },
            { value: '/api/v1/agriculture/cereals', label: 'Production céréalière' },
            { value: '/api/v1/agriculture/livestock', label: 'Élevage' },
        ],
        poverty: [
            { value: '/api/v1/poverty/rates', label: 'Taux de pauvreté' },
            { value: '/api/v1/poverty/regional', label: 'Pauvreté par région' },
        ],
        sirene: [
            { value: '/api/v1/sirene/search', label: 'Recherche entreprise' },
            { value: '/api/v1/sirene/siren/{siren}', label: 'Détails entreprise' },
            { value: '/api/v1/sirene/sectors', label: 'Statistiques par secteur' },
        ],
        bdm: [
            { value: '/api/v1/bdm/series/{code}', label: 'Série chronologique' },
            { value: '/api/v1/bdm/ihpc', label: 'Indice IHPC' },
            { value: '/api/v1/bdm/indicators', label: 'Liste des indicateurs' },
        ],
        localdata: [
            { value: '/api/v1/localdata/commune/{code}', label: 'Données communales' },
            { value: '/api/v1/localdata/regions', label: 'Données régionales' },
            { value: '/api/v1/localdata/provinces', label: 'Données provinciales' },
        ]
    };

    // Sample API responses for different endpoints
    const sampleResponses = {
        '/api/v1/demographics/population': {
            status: 200,
            data: {
                indicator: 'population_totale',
                source: 'INSD - Recensement 2019',
                last_updated: '2023-12-01',
                frequency: 'annual',
                unit: 'habitants',
                values: [
                    { year: 2019, region: 'National', value: 20870060 },
                    { year: 2020, region: 'National', value: 21510181 },
                    { year: 2021, region: 'Centre', value: 3542875 },
                    { year: 2021, region: 'Nord', value: 1965642 },
                ],
            },
            meta: { count: 4, rate_limit: { limit: 60, remaining: 59, reset: '2025-01-01T12:00:00Z' } },
        },
        '/api/v1/sirene/search': {
            status: 200,
            data: {
                total: 1247,
                results: [
                    {
                        siren: 'BF20230012345',
                        name: 'SOCIETE BURKINABE DE TEXTILE SA',
                        sector: 'Industrie textile',
                        creation_date: '2015-03-12',
                        status: 'active',
                        employees: 450,
                        region: 'Centre'
                    },
                    {
                        siren: 'BF20220098765',
                        name: 'AGRO-BUSINESS BURKINA SARL',
                        sector: 'Agriculture et agroalimentaire',
                        creation_date: '2018-07-25',
                        status: 'active',
                        employees: 120,
                        region: 'Hauts-Bassins'
                    }
                ]
            },
            meta: { count: 2, page: 1, total_pages: 624, rate_limit: { limit: 100, remaining: 99 } }
        },
        '/api/v1/bdm/ihpc': {
            status: 200,
            data: {
                indicator: 'ihpc',
                name: 'Indice Harmonisé des Prix à la Consommation',
                source: 'INSD - BDM',
                last_updated: '2025-01-15',
                frequency: 'monthly',
                base_year: 2021,
                values: [
                    { period: '2024-10', value: 112.4, variation_monthly: 0.8, variation_annual: 5.2 },
                    { period: '2024-11', value: 113.1, variation_monthly: 0.6, variation_annual: 5.4 },
                    { period: '2024-12', value: 113.8, variation_monthly: 0.6, variation_annual: 5.6 }
                ]
            },
            meta: { count: 3, rate_limit: { limit: 60, remaining: 58 } }
        },
        '/api/v1/localdata/commune/{code}': {
            status: 200,
            data: {
                commune: {
                    code: 'COM001',
                    name: 'Ouagadougou',
                    province: 'Kadiogo',
                    region: 'Centre',
                    population: 2415266,
                    superficie: 219.3,
                    maire: 'Armand Béouindé',
                    demographics: {
                        households: 402544,
                        density: 11015,
                        growth_rate: 4.2
                    },
                    infrastructure: {
                        schools: 487,
                        health_centers: 92,
                        markets: 28,
                        water_access: 89.5,
                        electricity_access: 76.3
                    },
                    economy: {
                        main_activities: ['Commerce', 'Services', 'Artisanat'],
                        unemployment_rate: 12.4
                    }
                }
            },
            meta: { rate_limit: { limit: 60, remaining: 57 } }
        }
    };

    // API catalog with new entries
    const apis = [
        {
            title: 'API Démographie',
            description: 'Accédez aux données démographiques: population, recensements, projections.',
            category: 'Population',
            endpoints: 15,
            rateLimit: '60/min',
            status: 'active',
            icon: Users,
            gradient: 'blue-gradient',
            features: [
                'Population totale et par région',
                'Projections démographiques',
                'Structure par âge et sexe',
                'Taux de croissance'
            ],
            useCases: [
                'Planification urbaine',
                'Études de marché',
                'Recherche académique'
            ],
            apiKey: 'demographics'
        },
        {
            title: 'API Agriculture',
            description: 'Données agricoles: production de coton, céréales, élevage, prix des denrées.',
            category: 'Agriculture',
            endpoints: 22,
            rateLimit: '60/min',
            status: 'active',
            icon: Wheat,
            gradient: 'green-gradient',
            features: [
                'Production céréalière',
                'Rendements agricoles',
                'Prix des denrées',
                'Statistiques d\'élevage'
            ],
            useCases: [
                'Analyse de marchés agricoles',
                'Prévisions de récolte',
                'Politique agricole'
            ],
            apiKey: 'agriculture'
        },
        {
            title: 'API Économie',
            description: 'Indicateurs économiques: PIB, inflation, commerce extérieur, emploi.',
            category: 'Économie',
            endpoints: 28,
            rateLimit: '60/min',
            status: 'active',
            icon: TrendingUp,
            gradient: 'orange-gradient',
            features: [
                'PIB et croissance',
                'Taux d\'inflation',
                'Commerce extérieur',
                'Emploi et chômage'
            ],
            useCases: [
                'Analyse économique',
                'Études d\'investissement',
                'Politique monétaire'
            ],
            apiKey: 'economy'
        },
        {
            title: 'API Santé',
            description: 'Indicateurs sanitaires: mortalité, vaccination, maladies, infrastructures.',
            category: 'Santé',
            endpoints: 18,
            rateLimit: '60/min',
            status: 'active',
            icon: Heart,
            gradient: 'red-gradient',
            features: [
                'Mortalité infantile',
                'Taux de vaccination',
                'Prévalence des maladies',
                'Infrastructure sanitaire'
            ],
            useCases: [
                'Planification sanitaire',
                'Recherche épidémiologique',
                'Évaluation de programmes'
            ],
            apiKey: 'health'
        },
        {
            title: 'API Éducation',
            description: 'Données éducatives: taux de scolarisation, alphabétisation, infrastructures.',
            category: 'Éducation',
            endpoints: 14,
            rateLimit: '60/min',
            status: 'active',
            icon: GraduationCap,
            gradient: 'cyan-gradient',
            features: [
                'Taux de scolarisation',
                'Alphabétisation',
                'Infrastructures scolaires',
                'Performance académique'
            ],
            useCases: [
                'Politique éducative',
                'Planification scolaire',
                'Évaluation de programmes'
            ],
            apiKey: 'education'
        },
        {
            title: 'API Infrastructure',
            description: 'Accès aux services: électricité, eau potable, routes, télécommunications.',
            category: 'Infrastructure',
            endpoints: 16,
            rateLimit: '60/min',
            status: 'beta',
            icon: Building,
            gradient: 'gray-gradient',
            features: [
                'Accès à l\'électricité',
                'Eau potable',
                'Réseau routier',
                'Télécommunications'
            ],
            useCases: [
                'Planification infrastructure',
                'Évaluation de projets',
                'Développement territorial'
            ],
            apiKey: 'infrastructure'
        },
        {
            title: 'API SIRENE BF',
            description: 'Accès automatisé aux informations d\'entreprises immatriculées. Stimule l\'innovation dans le secteur privé et la BI.',
            category: 'Entreprises',
            endpoints: 12,
            rateLimit: '100/min',
            status: 'active',
            icon: Briefcase,
            gradient: 'purple-gradient',
            features: [
                'Recherche d\'entreprises',
                'Informations détaillées (SIREN, secteur)',
                'Statistiques par secteur d\'activité',
                'Historique et modifications'
            ],
            useCases: [
                'Due diligence entreprises',
                'Business Intelligence',
                'Études sectorielles',
                'KYC et vérification'
            ],
            apiKey: 'sirene'
        },
        {
            title: 'API BDM',
            description: 'Banque de Données Macroéconomiques. Accès fluide aux séries chronologiques et indices (IHPC) pour les analystes.',
            category: 'Macroéconomie',
            endpoints: 25,
            rateLimit: '60/min',
            status: 'active',
            icon: BarChart3,
            gradient: 'indigo-gradient',
            features: [
                'Séries chronologiques complètes',
                'Indice IHPC et dérivés',
                'Agrégats macroéconomiques',
                'Export multi-formats (JSON, CSV, Excel)'
            ],
            useCases: [
                'Analyse économique avancée',
                'Modélisation macroéconomique',
                'Prévisions et tendances',
                'Tableaux de bord analytiques'
            ],
            apiKey: 'bdm'
        },
        {
            title: 'API Local Data',
            description: 'Accès aux données localisées au niveau communal. Essentiel pour le pilotage du développement local décentralisé.',
            category: 'Données locales',
            endpoints: 18,
            rateLimit: '60/min',
            status: 'active',
            icon: MapPin,
            gradient: 'teal-gradient',
            features: [
                'Données par commune (302 communes)',
                'Indicateurs socio-économiques locaux',
                'Infrastructure et équipements',
                'Géolocalisation et cartographie'
            ],
            useCases: [
                'Planification décentralisée',
                'Développement communal',
                'Cartographie interactive',
                'Suivi projets locaux'
            ],
            apiKey: 'localdata'
        },
        {
            title: 'Migration ODP 2.0',
            description: 'Support technique pour le déploiement du Portail Open Data 2.0 et d\'applications mobiles connectées.',
            category: 'Support technique',
            endpoints: 8,
            rateLimit: '30/min',
            status: 'beta',
            icon: Upload,
            gradient: 'amber-gradient',
            features: [
                'Migration données vers ODP 2.0',
                'APIs pour applications mobiles',
                'Synchronisation automatique',
                'Support CKAN 2.10+'
            ],
            useCases: [
                'Déploiement portail Open Data',
                'Développement apps mobiles',
                'Intégration données ouvertes',
                'Modernisation infrastructure'
            ],
            apiKey: 'migration'
        },
    ];

    // Filter APIs based on search and filters
    const filteredApis = apis.filter(api => {
        const matchesSearch = api.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            api.description.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesFilters = filters.length === 0 || filters.includes(api.category);
        return matchesSearch && matchesFilters;
    });

    // Toggle filter
    const toggleFilter = (category) => {
        if (filters.includes(category)) {
            setFilters(filters.filter(f => f !== category));
        } else {
            setFilters([...filters, category]);
        }
    };

    // Open API details modal and scroll to console
    const openApiDetails = (api) => {
        setSelectedApi(api);
        setShowApiModal(true);
        // Set first endpoint of this API
        const apiEndpoints = endpointsData[api.apiKey] || endpointsData.demographics;
        setSelectedEndpoint(apiEndpoints[0].value);
        setApiResponse(null);

        // Défilement vers la console
        scrollToConsole();
    };

    // Close API modal
    const closeApiModal = () => {
        setShowApiModal(false);
        setSelectedApi(null);
        setApiResponse(null);
        setLoading(false);
    };

    // Execute API request
    const executeRequest = () => {
        setLoading(true);
        setTimeout(() => {
            // Find matching response or use default
            const response = sampleResponses[selectedEndpoint] || sampleResponses['/api/v1/demographics/population'];
            setApiResponse(response);
            setLoading(false);
        }, 1500);
    };

    // Copy to clipboard
    const copyCode = (code) => {
        navigator.clipboard.writeText(code);
    };

    // Get endpoints for current API
    const getCurrentEndpoints = () => {
        if (!selectedApi) return endpointsData.demographics;
        return endpointsData[selectedApi.apiKey] || endpointsData.demographics;
    };

    // Code examples
    const codeExamples = {
        curl: `curl -X GET "https://api.insd.bf${selectedEndpoint}" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json"`,
        python: `import requests

url = "https://api.insd.bf${selectedEndpoint}"
headers = {
    "Authorization": "Bearer YOUR_API_KEY",
    "Content-Type": "application/json"
}

response = requests.get(url, headers=headers)
data = response.json()
print(data)`,
        javascript: `fetch('https://api.insd.bf${selectedEndpoint}', {
  method: 'GET',
  headers: {
    'Authorization': 'Bearer YOUR_API_KEY',
    'Content-Type': 'application/json'
  }
})
  .then(response => response.json())
  .then(data => console.log(data));`
    };

    return (
        <div className={`app-container ${darkMode ? 'dark' : ''}`}>
            {/* Navbar */}
            <nav className="navbar">
                <div className="nav-container">
                    <div className="nav-logo">
                        <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>
                            <div>
                                <div className="logo-text">INSData API</div>
                                <div className="logo-subtitle">Burkina Faso</div>
                            </div>
                        </Link>
                    </div>

                    <div className="nav-links">
                        <button
                            className="nav-link"
                            onClick={() => navigate('/')}
                        >
                            <Code size={18} />
                            <span>Catalogue API</span>
                        </button>
                        <button
                            className="nav-link"
                            onClick={() => navigate('/documentation')}
                        >
                            <BookOpen size={18} />
                            <span>Documentation</span>
                        </button>
                        <button
                            className="nav-link"
                            onClick={() => navigate('/dashboard')}
                        >
                            <LayoutDashboard size={18} />
                            <span>Tableau de bord</span>
                        </button>
                    </div>

                    <div className="nav-actions">
                        <button
                            onClick={() => setCurrentLang(currentLang === 'fr' ? 'en' : 'fr')}
                            className="nav-button"
                        >
                            <Globe size={20} />
                        </button>
                        <button
                            onClick={() => setDarkMode(!darkMode)}
                            className="nav-button"
                        >
                            {darkMode ? <Sun size={20} /> : <Moon size={20} />}
                        </button>
                        <button
                            className="login-button"
                            onClick={() => console.log('Connexion clicked')}
                        >
                            Se connecter
                        </button>
                        <button
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            className="mobile-menu-button"
                        >
                            {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
                        </button>
                    </div>
                </div>

                {mobileMenuOpen && (
                    <div className="mobile-menu">
                        <button
                            className="mobile-nav-link"
                            onClick={() => {
                                navigate('/');
                                setMobileMenuOpen(false);
                            }}
                        >
                            <Code size={18} />
                            <span>Catalogue API</span>
                        </button>
                        <button
                            className="mobile-nav-link"
                            onClick={() => {
                                navigate('/documentation');
                                setMobileMenuOpen(false);
                            }}
                        >
                            <BookOpen size={18} />
                            <span>Documentation</span>
                        </button>
                        <button
                            className="mobile-nav-link"
                            onClick={() => {
                                console.log('Sandbox clicked');
                                setMobileMenuOpen(false);
                            }}
                        >
                            <TestTube size={18} />
                            <span>Sandbox</span>
                        </button>
                        <button
                            className="mobile-nav-link"
                            onClick={() => {
                                navigate('/dashboard');
                                setMobileMenuOpen(false);
                            }}
                        >
                            <LayoutDashboard size={18} />
                            <span>Tableau de bord</span>
                        </button>
                    </div>
                )}
            </nav>

            {/* Hero Section */}
            <section className="hero">
                <div className="hero-pattern"></div>
                <div className="hero-content">
                    <h1 className="hero-title">
                        Plateforme API Statistique
                    </h1>
                    <h2 className="hero-subtitle">
                        du Burkina Faso
                    </h2>
                    <p className="hero-description">
                        Accédez aux données démographiques, économiques, agricoles et sociales via des API
                        modernes, sécurisées et documentées. Construisez des applications innovantes avec
                        les statistiques officielles de l'INSD.
                    </p>
                    <div className="hero-buttons">
                        <button
                            className="primary-button"
                            onClick={() => navigate('/documentation')}
                        >
                            Commencer maintenant
                            <ArrowRight size={20} />
                        </button>
                        <button
                            className="secondary-button"
                            onClick={() => navigate('/documentation')}
                        >
                            Voir la documentation
                        </button>
                    </div>

                    <div className="stats-grid">
                        {[
                            { icon: Database, label: 'Jeux de données', value: '150+' },
                            { icon: TrendingUp, label: 'Indicateurs', value: '2,000+' },
                            { icon: Users, label: 'Utilisateurs actifs', value: '500+' },
                            { icon: Zap, label: 'Requêtes/jour', value: '50k+' },
                        ].map((stat, index) => (
                            <div
                                key={index}
                                className="stat-card fade-in"
                                style={{ animationDelay: `${index * 100}ms` }}
                            >
                                <div className="stat-icon-container">
                                    <stat.icon className="stat-icon" />
                                </div>
                                <div className="stat-value">{stat.value}</div>
                                <div className="stat-label">{stat.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Main Content */}
            <div className="main-content">
                {/* Search Bar */}
                <div className="search-section">
                    <div className="search-container">
                        <div className="search-input-container">
                            <Search className="search-icon" size={20} />
                            <input
                                type="text"
                                placeholder="Rechercher des APIs, indicateurs, jeux de données..."
                                className="search-input"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && console.log('Search:', searchTerm)}
                            />
                        </div>
                        <button
                            className="filter-button"
                            onClick={() => setFilters([])}
                        >
                            <Filter size={18} />
                            <span>{filters.length > 0 ? `${filters.length} Filtre${filters.length > 1 ? 's' : ''}` : 'Filtres'}</span>
                            {filters.length > 0 ? <X size={18} /> : <ChevronDown size={18} />}
                        </button>
                        <button
                            className="search-button"
                            onClick={() => console.log('Search:', searchTerm)}
                        >
                            Rechercher
                        </button>
                    </div>

                    {filters.length > 0 && (
                        <div className="filter-tags">
                            {filters.map((filter) => (
                                <span
                                    key={filter}
                                    className="filter-tag"
                                >
                                    {filter}
                                    <button
                                        onClick={() => toggleFilter(filter)}
                                        className="remove-filter"
                                    >
                                        <X size={14} />
                                    </button>
                                </span>
                            ))}
                        </div>
                    )}
                </div>

                {/* API Catalog */}
                <section className="fade-in">
                    <h2 className="section-title">Catalogue des API</h2>
                    <p className="section-description">
                        Explorez nos API pour accéder aux statistiques officielles du Burkina Faso
                    </p>

                    {/* Filter Categories */}
                    <div className="filter-categories">
                        {['Population', 'Agriculture', 'Économie', 'Santé', 'Éducation', 'Infrastructure', 'Entreprises', 'Macroéconomie', 'Données locales', 'Support technique']
                            .map((category) => (
                                <button
                                    key={category}
                                    onClick={() => toggleFilter(category)}
                                    className={`category-filter ${filters.includes(category) ? 'active' : ''}`}
                                >
                                    {category}
                                </button>
                            ))}
                    </div>

                    <div className="api-grid">
                        {filteredApis.map((api, index) => (
                            <div
                                key={index}
                                className="api-card fade-in"
                                style={{ animationDelay: `${index * 100}ms` }}
                            >
                                <div className={`api-card-header ${api.gradient}`}>
                                    <api.icon className="api-icon" />
                                    <span className={`api-status ${api.status}`}>
                                        {api.status.toUpperCase()}
                                    </span>
                                    <h3 className="api-card-title">{api.title}</h3>
                                    <p className="api-card-category">{api.category}</p>
                                </div>

                                <div className="api-card-body">
                                    <p className="api-card-description">
                                        {api.description}
                                    </p>
                                    <div className="api-meta">
                                        <div className="api-meta-item">
                                            <Database size={16} />
                                            <span>{api.endpoints} endpoints</span>
                                        </div>
                                        <div className="api-meta-item">
                                            <Clock size={16} />
                                            <span>{api.rateLimit}</span>
                                        </div>
                                    </div>
                                    <div className="api-actions">
                                        <button
                                            className="api-explore-button"
                                            onClick={() => openApiDetails(api)}
                                        >
                                            Explorer l'API
                                        </button>
                                        <button
                                            className="api-external-button"
                                            onClick={() => console.log(`External link for ${api.title}`)}
                                        >
                                            <ExternalLink size={16} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Data Visualization */}
                <section className="viz-section fade-in">
                    <h2 className="section-title">Visualisation des données en direct</h2>

                    <div className="viz-tabs">
                        {['poverty', 'agriculture', 'economy', 'health'].map((tab) => (
                            <button
                                key={tab}
                                onClick={() => setActiveTab(tab)}
                                className={`viz-tab ${activeTab === tab ? 'active' : ''}`}
                            >
                                {tab === 'poverty' && 'Pauvreté'}
                                {tab === 'agriculture' && 'Agriculture'}
                                {tab === 'economy' && 'Économie'}
                                {tab === 'health' && 'Santé'}
                            </button>
                        ))}
                    </div>

                    <div className="viz-content">
                        {activeTab === 'poverty' && (
                            <div className="fade-in">
                                <h3 className="viz-chart-title">
                                    Évolution du taux de pauvreté (2015-2023)
                                </h3>
                                <ResponsiveContainer width="100%" height={300}>
                                    <LineChart data={povertyData}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="year" />
                                        <YAxis />
                                        <Tooltip />
                                        <Legend />
                                        <Line
                                            type="monotone"
                                            dataKey="rate"
                                            stroke="#EF2B2D"
                                            strokeWidth={3}
                                            name="Taux de pauvreté (%)"
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        )}

                        {activeTab === 'agriculture' && (
                            <div className="fade-in">
                                <h3 className="viz-chart-title">
                                    Production de coton (millions de balles)
                                </h3>
                                <ResponsiveContainer width="100%" height={300}>
                                    <BarChart data={cottonData}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="year" />
                                        <YAxis />
                                        <Tooltip />
                                        <Legend />
                                        <Bar dataKey="production" fill="#00A86B" name="Production (M balles)" />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        )}

                        {activeTab === 'economy' && (
                            <div className="fade-in">
                                <h3 className="viz-chart-title">
                                    Répartition sectorielle du PIB
                                </h3>
                                <ResponsiveContainer width="100%" height={300}>
                                    <PieChart>
                                        <Pie
                                            data={sectorData}
                                            cx="50%"
                                            cy="50%"
                                            labelLine={false}
                                            label={(entry) => `${entry.name}: ${entry.value}%`}
                                            outerRadius={100}
                                            fill="#8884d8"
                                            dataKey="value"
                                        >
                                            {sectorData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={entry.color} />
                                            ))}
                                        </Pie>
                                        <Tooltip />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                        )}

                        {activeTab === 'health' && (
                            <div className="fade-in">
                                <div className="health-grid">
                                    {healthData.map((item, index) => (
                                        <div
                                            key={item.indicator}
                                            className="health-card fade-in"
                                            style={{ animationDelay: `${index * 100}ms` }}
                                        >
                                            <div className="health-indicator">{item.indicator}</div>
                                            <div className="health-value">
                                                {item.value} {item.unit}
                                            </div>
                                            <div className={`health-trend ${item.trend}`}>
                                                {item.trend === 'up' ? '↑' : '↓'} Tendance {item.trend === 'up' ? 'positive' : 'négative'}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </section>

                {/* Interactive Console */}
                <section
                    ref={consoleSectionRef}
                    className={`console-section fade-in ${showApiModal ? 'active' : ''}`}
                >
                    {showApiModal && selectedApi ? (
                        <>
                            <div className="modal-header">
                                <h2 className="section-title">
                                    {selectedApi.title} - Console interactive
                                </h2>
                                <button
                                    onClick={closeApiModal}
                                    className="close-modal-button"
                                >
                                    <X size={24} />
                                </button>
                            </div>

                            <div className="api-details-grid">
                                <div className="api-detail-section">
                                    <h3>Fonctionnalités</h3>
                                    <ul>
                                        {selectedApi.features.map((feature, idx) => (
                                            <li key={idx}>{feature}</li>
                                        ))}
                                    </ul>
                                </div>
                                <div className="api-detail-section">
                                    <h3>Cas d'usage</h3>
                                    <ul>
                                        {selectedApi.useCases.map((useCase, idx) => (
                                            <li key={idx}>{useCase}</li>
                                        ))}
                                    </ul>
                                </div>
                            </div>

                            <div className="request-builder">
                                <div className="request-controls">
                                    <span className="request-method">GET</span>
                                    <select
                                        className="endpoint-select"
                                        value={selectedEndpoint}
                                        onChange={(e) => setSelectedEndpoint(e.target.value)}
                                    >
                                        {getCurrentEndpoints().map((endpoint) => (
                                            <option key={endpoint.value} value={endpoint.value}>
                                                {endpoint.label}
                                            </option>
                                        ))}
                                    </select>
                                    <button
                                        onClick={executeRequest}
                                        disabled={loading}
                                        className="execute-button"
                                    >
                                        <Play size={18} />
                                        {loading ? 'Chargement...' : 'Exécuter'}
                                    </button>
                                    <button
                                        onClick={() => {
                                            setApiResponse(null);
                                            setLoading(false);
                                        }}
                                        className="reset-button"
                                    >
                                        <RotateCcw size={18} />
                                        Réinitialiser
                                    </button>
                                </div>

                                <div>
                                    <div className="code-tabs">
                                        {['curl', 'python', 'javascript'].map((tab) => (
                                            <button
                                                key={tab}
                                                onClick={() => setCodeTab(tab)}
                                                className={`code-tab ${codeTab === tab ? 'active' : ''}`}
                                            >
                                                {tab === 'curl' && 'cURL'}
                                                {tab === 'python' && 'Python'}
                                                {tab === 'javascript' && 'JavaScript'}
                                            </button>
                                        ))}
                                    </div>
                                    <div className="code-container">
                                        <button
                                            onClick={() => copyCode(codeExamples[codeTab])}
                                            className="copy-button"
                                        >
                                            <Copy size={14} />
                                            Copier
                                        </button>
                                        <pre className="code-block">
                                            <code>{codeExamples[codeTab]}</code>
                                        </pre>
                                    </div>
                                </div>
                            </div>

                            {apiResponse && (
                                <div className="response-section fade-in">
                                    <div className="response-header">
                                        <h3 className="section-title">Réponse</h3>
                                        <div style={{display: 'flex', gap: '0.5rem'}}>
                                            <span className="response-status">200 OK</span>
                                            <span className="response-time">65ms</span>
                                        </div>
                                    </div>
                                    <div className="code-container">
                                        <button
                                            onClick={() => copyCode(JSON.stringify(apiResponse, null, 2))}
                                            className="copy-button"
                                        >
                                            <Copy size={14} />
                                            Copier
                                        </button>
                                        <pre className="code-block">
                                            <code>{JSON.stringify(apiResponse, null, 2)}</code>
                                        </pre>
                                    </div>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="console-placeholder">
                            <h3>Sélectionnez une API pour explorer ses endpoints</h3>
                            <p>Cliquez sur "Explorer l'API" dans une carte du catalogue ci-dessus pour commencer</p>
                        </div>
                    )}
                </section>

                {/* CTA Section */}
                <section className="cta-section fade-in">
                    <h2 className="section-title">Prêt à commencer ?</h2>
                    <p className="section-description">
                        Créez votre compte gratuit et obtenez votre clé API en moins de 2 minutes.
                        Accédez immédiatement à toutes nos données ouvertes.
                    </p>
                    <div className="cta-buttons">
                        <button
                            className="primary-button"
                            onClick={() => console.log('Créer un compte gratuit')}
                        >
                            Créer un compte gratuit
                        </button>
                        <button
                            className="secondary-button"
                            style={{color: '#2d3748', borderColor: '#e2e8f0'}}
                            onClick={() => navigate('/documentation')}
                        >
                            Consulter la documentation
                        </button>
                    </div>
                </section>
            </div>

            {/* Footer */}
            <footer className="footer">
                <div className="footer-content">
                    <div className="footer-column">
                        <h3>À propos</h3>
                        <p>
                            Plateforme API de l'Institut National de la Statistique et de la Démographie
                            du Burkina Faso.
                        </p>
                    </div>
                    <div className="footer-column">
                        <h3>Ressources</h3>
                        <ul>
                            <li><button className="footer-link" onClick={() => navigate('/documentation')}>Documentation</button></li>
                            <li><button className="footer-link" onClick={() => console.log('Guides de démarrage')}>Guides de démarrage</button></li>
                            <li><button className="footer-link" onClick={() => console.log('Exemples de code')}>Exemples de code</button></li>
                            <li><button className="footer-link" onClick={() => console.log('FAQ')}>FAQ</button></li>
                        </ul>
                    </div>
                    <div className="footer-column">
                        <h3>Support</h3>
                        <ul>
                            <li><button className="footer-link" onClick={() => console.log('Contact')}>Contact</button></li>
                            <li><button className="footer-link" onClick={() => console.log('Forum communautaire')}>Forum communautaire</button></li>
                            <li><button className="footer-link" onClick={() => console.log('Statut du service')}>Statut du service</button></li>
                            <li><button className="footer-link" onClick={() => console.log('Signaler un bug')}>Signaler un bug</button></li>
                        </ul>
                    </div>
                    <div className="footer-column">
                        <h3>Légal</h3>
                        <ul>
                            <li><button className="footer-link" onClick={() => console.log('Conditions d\'utilisation')}>Conditions d'utilisation</button></li>
                            <li><button className="footer-link" onClick={() => console.log('Politique de confidentialité')}>Politique de confidentialité</button></li>
                            <li><button className="footer-link" onClick={() => console.log('Licence des données')}>Licence des données</button></li>
                        </ul>
                    </div>
                </div>
                <div className="footer-bottom">
                    <p>© 2025 INSData API - Institut National de la Statistique et de la Démographie du Burkina Faso. Tous droits réservés.</p>
                </div>
            </footer>
        </div>
    );
}

export default INSDAPIPortal;